import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Box, 
  Grid, 
  Typography, 
  TextField,
  InputAdornment,
  IconButton,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Button,
  Paper,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip,
  Chip,
  Snackbar,
  Autocomplete,
  FormControlLabel,
  useMediaQuery,
  Card,
  CardContent,
  CardActions,
  CircularProgress,
  Switch
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { 
  Search,
  Clear,
  Add,
  Edit,
  Delete,
  Person,
  LocationCity,
  Devices as DevicesIcon,
  Email,
  Check,
  Close,
  Badge,
  Visibility,
  Upload,
  Download,
  CloudUpload,
} from '@mui/icons-material';
import MuiAlert from '@mui/material/Alert';
import userService from '../services/userService';
import accountService from '../services/accountService';
import siteService from '../services/siteService';
import deviceService from '../services/deviceService';
import { useRefresh } from '../context/RefreshContext';

// Alert component for Snackbar
const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} ref={ref} variant="filled" {...props} />;
});

const Users = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const { refreshCounter, refreshData } = useRefresh();
  const { user: authUser, refreshPermissions } = useAuth();
  const isAdmin = authUser?.role === 'admin';
  const isAdminOrOperator = isAdmin || authUser?.role === 'operator';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  // State variables
  const [loading, setLoading] = useState(true);
  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalUsers, setTotalUsers] = useState(0);
  const [accounts, setAccounts] = useState([]);
  const [sites, setSites] = useState([]);
  const [devices, setDevices] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });
  
  // CSV Import state
  const [csvImportOpen, setCsvImportOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [csvPreview, setCsvPreview] = useState([]);
  const [csvImporting, setCsvImporting] = useState(false);
  
  // Form data state
  const [userFormData, setUserFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    role: 'end_user',
    active: true,
    allowEmailNotifications: false,
    accountIds: [],
    siteIds: [],
    deviceIds: []
  });

  const normalizeIdStrings = (values = []) => Array.from(new Set(
    (Array.isArray(values) ? values : [])
      .map((value) => String(value ?? '').trim())
      .filter((value) => value.length > 0)
  ));

  const loadAllDeviceOptions = async () => {
    const pageLimit = 1000;
    const maxPages = 50;
    let offset = 0;
    let page = 0;
    let total = null;
    const byId = new Map();

    while (page < maxPages) {
      const resp = await userService.fetchDeviceOptions({
        limit: pageLimit,
        offset
      });
      const list = Array.isArray(resp?.devices) ? resp.devices : [];

      list.forEach((d) => {
        if (d?.id === undefined || d?.id === null) return;
        byId.set(String(d.id), d);
      });

      const respTotal = Number(resp?.total);
      if (Number.isFinite(respTotal)) total = respTotal;

      if (list.length === 0) break;
      offset += list.length;
      page += 1;

      if (total !== null && offset >= total) break;
      if (list.length < pageLimit) break;
    }

    return Array.from(byId.values());
  };

  // Load data
  const loadData = async (pageArg = page, pageSizeArg = pageSize, search = '') => {
    setLoading(true);
    try {
      // Fetch all sites and devices for associations
      const [accountsData, sitesData] = await Promise.all([
        accountService.fetchAccounts(),
        siteService.fetchSites()
      ]);
      setAccounts(Array.isArray(accountsData) ? accountsData : []);
      setSites(sitesData);

      const allDevices = await loadAllDeviceOptions();
      setDevices(allDevices);
      
      // Fetch users with pagination and search
      let usersData = [];
      if (search.trim() !== '') {
        // If searching, fetch all users and filter client-side
        const resp = await userService.fetchUsers({ limit: 10000 });
        usersData = resp.users || [];
        
        // Filter users by search term
        const term = search.toLowerCase();
        usersData = usersData.filter(user => 
          (user.firstName && user.firstName.toLowerCase().includes(term)) || 
          (user.lastName && user.lastName.toLowerCase().includes(term)) ||
          (user.email && user.email.toLowerCase().includes(term))
        );
        
        // Apply paging to filtered results
        const startIdx = (pageArg - 1) * pageSizeArg;
        const endIdx = startIdx + pageSizeArg;
        setUsers(usersData);
        setFilteredUsers(usersData.slice(startIdx, endIdx));
        setTotalUsers(usersData.length);
      } else {
        // Fetch paged users from API
        const resp = await userService.fetchUsers({
          limit: pageSizeArg,
          offset: (pageArg - 1) * pageSizeArg
        });
        usersData = resp.users || [];
        setUsers(usersData);
        setFilteredUsers(usersData);
        setTotalUsers(resp.total || usersData.length);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load users. Please try again.',
        severity: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData(page, pageSize, searchTerm);
  }, [refreshCounter, page, pageSize, searchTerm]);

  // Event handlers
  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
    setPage(1); // Reset to first page when searching
  };

  const handleClearSearch = () => {
    setSearchTerm('');
  };

  const handleAddUser = () => {
    setUserFormData({
      firstName: '',
      lastName: '',
      email: '',
      role: 'end_user',
      active: true,
      allowEmailNotifications: false,
      accountIds: [],
      siteIds: [],
      deviceIds: []
    });
    setDialogType('add');
    setOpenDialog(true);
  };

  const handleEditUser = (user, event) => {
    event.stopPropagation();
    setCurrentUser(user);
    setUserFormData({
      firstName: user.firstName || '',
      lastName: user.lastName || '',
      email: user.email || '',
      role: user.role || 'end_user',
      active: user.active !== false,
      allowEmailNotifications: user.allowEmailNotifications === true,
      accountIds: normalizeIdStrings(user.accounts?.map((account) => account?.id) || []),
      siteIds: normalizeIdStrings(user.sites?.map((site) => site?.id) || []),
      deviceIds: normalizeIdStrings(user.devices?.map((device) => device?.id) || [])
    });
    setDialogType('edit');
    setOpenDialog(true);
  };

  const handleDeleteUser = (user, event) => {
    event.stopPropagation();
    if (!isAdmin) return;
    setCurrentUser(user);
    setDialogType('delete');
    setOpenDialog(true);
  };

  const handleResetPasswordAndEmail = async (user, event) => {
    event.stopPropagation();
    try {
      await userService.resetUserPasswordAndEmail(user.id, { mode: 'default' });
      setSnackbar({
        open: true,
        message: 'Login email sent (password reset)',
        severity: 'success'
      });
      await loadData(page, pageSize, searchTerm);
    } catch (error) {
      console.error('Error resetting password and sending email:', error);
      setSnackbar({
        open: true,
        message: 'Failed to send login email.',
        severity: 'error'
      });
    }
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleUserFormChange = (event) => {
    const { name, value, checked } = event.target;
    
    // Handle checkbox/switch fields
    if (name === 'active' || name === 'allowEmailNotifications') {
      setUserFormData({
        ...userFormData,
        [name]: checked
      });
    } else {
      setUserFormData({
        ...userFormData,
        [name]: value
      });
    }
  };

  const handleSiteChange = (event, newValue) => {
    const nextSiteIds = normalizeIdStrings(newValue.map((site) => site?.id));

    setUserFormData((prev) => ({
      ...prev,
      siteIds: nextSiteIds
    }));

    (async () => {
      if (!nextSiteIds.length) return;
      try {
        setSubmitting(true);

        const results = await Promise.all(
          nextSiteIds.map(async (siteId) => {
            try {
              return await deviceService.fetchDevicesBySite(siteId);
            } catch (e) {
              return [];
            }
          })
        );

        const siteDevices = results.flat();
        const siteDeviceIds = normalizeIdStrings(siteDevices.map((d) => d?.id));

        // Ensure device Autocomplete options include the fetched devices
        if (siteDevices.length > 0) {
          setDevices((prev) => {
            const byId = new Map((prev || []).map((d) => [String(d.id), d]));
            siteDevices.forEach((d) => {
              const id = String(d?.id ?? '').trim();
              if (!id) return;
              byId.set(id, d);
            });
            return Array.from(byId.values());
          });
        }

        setUserFormData((prev) => ({
          ...prev,
          deviceIds: normalizeIdStrings([...(prev.deviceIds || []), ...siteDeviceIds])
        }));
      } finally {
        setSubmitting(false);
      }
    })();
  };

  const handleDeviceChange = (event, newValues) => {
    setUserFormData((prev) => ({
      ...prev,
      deviceIds: normalizeIdStrings(newValues.map((device) => device?.id))
    }));
  };

  const handleAccountChange = (event, newValue) => {
    setUserFormData((prev) => ({
      ...prev,
      accountIds: normalizeIdStrings(newValue.map((account) => account?.id))
    }));
  };

  // Function to add all devices from a specific site
  const handleAddAllDevicesFromSite = async (siteId) => {
    if (!siteId) return;
    
    try {
      // Show loading
      setSubmitting(true);
      
      // Fetch all devices for the selected site
      const siteDevices = await deviceService.fetchDevicesBySite(siteId);
      
      if (siteDevices.length === 0) {
        setSnackbar({
          open: true,
          message: 'No devices found for the selected site.',
          severity: 'info'
        });
        return;
      }
      
      // Get the device IDs
      const siteDeviceIds = normalizeIdStrings(siteDevices.map((device) => device?.id));

      // Ensure device Autocomplete options include the fetched devices
      setDevices((prev) => {
        const byId = new Map((prev || []).map((d) => [String(d.id), d]));
        siteDevices.forEach((d) => {
          const id = String(d?.id ?? '').trim();
          if (!id) return;
          byId.set(id, d);
        });
        return Array.from(byId.values());
      });
      
      // Add these devices to current selection without duplicates
      setUserFormData((prev) => ({
        ...prev,
        deviceIds: normalizeIdStrings([...(prev.deviceIds || []), ...siteDeviceIds])
      }));
      
      setSnackbar({
        open: true,
        message: `Added ${siteDeviceIds.length} devices from the selected site`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error fetching site devices:', error);
      setSnackbar({
        open: true,
        message: 'Failed to load devices for this site.',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitUser = async () => {
    setSubmitting(true);
    try {
      const normalizedSiteIds = normalizeIdStrings(userFormData.siteIds || []);
      const normalizedDeviceIds = normalizeIdStrings(userFormData.deviceIds || []);
      const normalizedAccountIds = normalizeIdStrings(userFormData.accountIds || []);

      const userPayload = {
        ...userFormData,
        accountIds: normalizedAccountIds,
        siteIds: normalizedSiteIds,
        deviceIds: normalizedDeviceIds
      };

      // Validate required fields
      if (!userFormData.firstName || !userFormData.lastName || !userFormData.email) {
        setSnackbar({
          open: true,
          message: 'Please fill in all required fields',
          severity: 'error'
        });
        setSubmitting(false);
        return;
      }
      
      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(userFormData.email)) {
        setSnackbar({
          open: true,
          message: 'Please enter a valid email address',
          severity: 'error'
        });
        setSubmitting(false);
        return;
      }

      if (dialogType === 'add') {
        // Create new user
        const newUser = await userService.createUser(userPayload);
        
        // Associate sites and devices if selected
        if (normalizedSiteIds.length > 0) {
          await userService.updateUserSites(newUser.id, normalizedSiteIds);
        }

        if (normalizedAccountIds.length > 0) {
          await userService.updateUserAccounts(newUser.id, normalizedAccountIds);
        }
        
        if (normalizedDeviceIds.length > 0) {
          await userService.updateUserDevices(newUser.id, normalizedDeviceIds);
        }
        
        setSnackbar({
          open: true,
          message: 'User created successfully',
          severity: 'success'
        });
      } else if (dialogType === 'edit') {
        // Update user
        await userService.updateUser(currentUser.id, userPayload);
        
        // Update site associations
        await userService.updateUserSites(currentUser.id, normalizedSiteIds);

        // Update account associations
        await userService.updateUserAccounts(currentUser.id, normalizedAccountIds);

        if (authUser?.id === currentUser.id) {
          await refreshPermissions();
        }
        
        // Update device associations
        await userService.updateUserDevices(currentUser.id, normalizedDeviceIds);
        
        setSnackbar({
          open: true,
          message: 'User updated successfully',
          severity: 'success'
        });
      }
      
      // Close dialog and refresh data
      setOpenDialog(false);
      
      // Force a complete data reload, including all associations
      setTimeout(() => {
        loadData(page, pageSize, searchTerm);
      }, 300);
    } catch (error) {
      console.error('Error submitting user:', error);
      setSnackbar({
        open: true,
        message: `Failed to ${dialogType === 'add' ? 'create' : 'update'} user. Please try again.`,
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteSubmit = async () => {
    if (!currentUser) return;
    
    setSubmitting(true);
    try {
      await userService.deleteUser(currentUser.id);
      setSnackbar({
        open: true,
        message: 'User deleted successfully',
        severity: 'success'
      });
      setOpenDialog(false);
      refreshData();
    } catch (error) {
      console.error('Error deleting user:', error);
      setSnackbar({
        open: true,
        message: 'Failed to delete user. Please try again.',
        severity: 'error'
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({
      ...snackbar,
      open: false
    });
  };

  // Helper function to get role display name
  const getRoleDisplayName = (role) => {
    switch (role) {
      case 'admin':
        return 'Admin';
      case 'super_user':
        return 'Super User';
      case 'operator':
        return 'Operator';
      case 'end_user':
        return 'User';
      default:
        return role;
    }
  };

  // CSV Import handlers
  const handleCsvImportOpen = () => {
    setCsvImportOpen(true);
    setCsvFile(null);
    setCsvPreview([]);
  };

  const handleCsvImportClose = () => {
    setCsvImportOpen(false);
    setCsvFile(null);
    setCsvPreview([]);
  };

  const handleCsvFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
      
      // Preview CSV content
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target.result;
        const lines = text.split('\n').filter(line => line.trim());
        const preview = lines.slice(0, 6); // Show first 5 rows + header
        setCsvPreview(preview);
      };
      reader.readAsText(file);
    } else {
      setSnackbar({
        open: true,
        message: 'Please select a valid CSV file',
        severity: 'error'
      });
    }
  };

  const handleCsvImport = async () => {
    if (!csvFile) {
      setSnackbar({
        open: true,
        message: 'Please select a CSV file first',
        severity: 'error'
      });
      return;
    }

    setCsvImporting(true);
    try {
      const result = await userService.importUsersFromCsv(csvFile);
      
      setSnackbar({
        open: true,
        message: `Successfully imported ${result.imported} users. ${result.skipped > 0 ? `Skipped ${result.skipped} rows.` : ''}`,
        severity: 'success'
      });
      
      // Refresh data and close dialog
      await loadData();
      handleCsvImportClose();
      
    } catch (error) {
      console.error('CSV import error:', error);
      setSnackbar({
        open: true,
        message: error.response?.data?.error || 'Failed to import CSV. Please try again.',
        severity: 'error'
      });
    } finally {
      setCsvImporting(false);
    }
  };

  const downloadUsersTemplate = () => {
    const csvContent = `firstName,lastName,email,role,active,siteDescriptions,deviceSerialNumbers
"John","Doe","john.doe@example.com","end_user","true","Downtown Office Complex;Riverside Apartments","WM001;FS001"
"Jane","Smith","jane.smith@example.com","super_user","true","Manufacturing Plant A","WM003;PS002;FS003"
"Admin","User","admin@example.com","admin","true","",""`;
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'users_template.csv';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <Box sx={{ 
      p: { xs: 1, sm: 2 },
      maxWidth: '100%',
      mx: 'auto',
      width: '100%',
      boxSizing: 'border-box'
    }}>
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap',
        justifyContent: 'space-between', 
        alignItems: { xs: 'stretch', sm: 'center' },
        gap: 1,
        mb: 2
      }}>
        <Typography variant={isMobile ? 'h5' : 'h4'} component="h1" gutterBottom sx={{ mr: 1 }}>
          Users
        </Typography>
        <Box sx={{ 
          display: 'flex', 
          flexWrap: 'wrap', 
          gap: 1, 
          flexBasis: { xs: '100%', sm: 'auto' },
          mt: { xs: 0.5, sm: 0 },
          justifyContent: { xs: 'flex-start', sm: 'flex-end' } 
        }}>
          {isAdminOrOperator && (
            <Button 
              variant="contained" 
              color="primary" 
              startIcon={<Add />} 
              onClick={handleAddUser}
              size={isMobile ? 'small' : 'medium'}
            >
              Add User
            </Button>
          )}
          {isAdminOrOperator && (
            <Button
              variant="outlined"
              color="primary"
              startIcon={<Upload />}
              onClick={handleCsvImportOpen}
              size={isMobile ? 'small' : 'medium'}
            >
              Import CSV
            </Button>
          )}
        </Box>
      </Box>

      {/* Search */}
      <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          variant="outlined"
          size={isMobile ? 'small' : 'medium'}
          placeholder="Search Users..."
          value={searchTerm}
          onChange={handleSearchChange}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton onClick={handleClearSearch} edge="end" size={isMobile ? 'small' : 'medium'}>
                  <Clear />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
      </Paper>

      {/* Loading indicator */}
      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* No results message */}
      {!loading && filteredUsers.length === 0 && (
        <Paper sx={{ p: 3, textAlign: 'center', borderRadius: 2 }}>
          <Typography variant="h6">No users found</Typography>
          <Typography variant="body2" color="textSecondary">
            {searchTerm ? 'Try a different search term' : 'Add users to get started'}
          </Typography>
        </Paper>
      )}

      {/* User list - Cards for mobile, Table for desktop */}
      {!loading && filteredUsers.length > 0 && (
        isMobile ? (
          <Box sx={{ mb: 2 }}>
            {filteredUsers.map(user => (
              <Card key={user.id} sx={{ mb: 2, borderRadius: 2 }}>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                    <Typography variant="h6" component="div">
                      {user.firstName} {user.lastName}
                    </Typography>
                    <Chip 
                      label={user.active ? 'Active' : 'Inactive'} 
                      color={user.active ? 'success' : 'default'}
                      size="small"
                    />
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Email fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {user.email}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <Badge fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {getRoleDisplayName(user.role)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                    <LocationCity fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {user.sites?.length || 0} Sites
                    </Typography>
                  </Box>
                  
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <DevicesIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {user.devices?.length || 0} Devices
                    </Typography>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                    <Typography variant="body2" color="text.secondary">
                      Email Alerts: {user.allowEmailNotifications ? 'On' : 'Off'}
                    </Typography>
                  </Box>
                </CardContent>
                <CardActions>
                  {isAdminOrOperator && (
                    <Button 
                      size="small" 
                      startIcon={<Edit />} 
                      onClick={(e) => handleEditUser(user, e)}
                    >
                      Edit
                    </Button>
                  )}
                  {isAdmin && (
                    <Button 
                      size="small" 
                      startIcon={<Delete />} 
                      color="error"
                      onClick={(e) => handleDeleteUser(user, e)}
                    >
                      Delete
                    </Button>
                  )}
                </CardActions>
              </Card>
            ))}
          </Box>
        ) : (
          <TableContainer component={Paper} sx={{ mb: 4, borderRadius: 2, overflowX: 'auto' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Email</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Email Alerts</TableCell>
                  <TableCell>Accounts</TableCell>
                  <TableCell>Sites</TableCell>
                  <TableCell>Devices</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredUsers.map(user => (
                  <TableRow 
                    key={user.id}
                    hover
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Person sx={{ mr: 1, color: 'primary.main' }} />
                        {user.firstName} {user.lastName}
                      </Box>
                    </TableCell>
                    <TableCell>{user.email}</TableCell>
                    <TableCell>{getRoleDisplayName(user.role)}</TableCell>
                    <TableCell>
                      <Chip 
                        label={user.active ? 'Active' : 'Inactive'} 
                        color={user.active ? 'success' : 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={user.allowEmailNotifications ? 'On' : 'Off'}
                        color={user.allowEmailNotifications ? 'success' : 'default'}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      <Tooltip title={user.accounts?.length > 0 ? user.accounts.map(account => account.name).join(', ') : 'No accounts'}>
                        <Chip
                          label={`${user.accounts?.length || 0} Accounts`}
                          size="small"
                          variant="outlined"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={user.sites?.length > 0 ? user.sites.map(site => site.description).join(', ') : 'No sites'}>
                        <Chip 
                          label={`${user.sites?.length || 0} Sites`} 
                          size="small" 
                          icon={<LocationCity />}
                          variant="outlined"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Tooltip title={user.devices?.length > 0 ? user.devices.map(device => device.description).join(', ') : 'No devices'}>
                        <Chip 
                          label={`${user.devices?.length || 0} Devices`} 
                          size="small"
                          icon={<DevicesIcon />}
                          variant="outlined"
                        />
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        {isAdminOrOperator && (
                          <Tooltip title="Edit User">
                            <IconButton 
                              size="small" 
                              color="primary"
                              onClick={(e) => handleEditUser(user, e)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isAdminOrOperator && ['end_user', 'super_user'].includes(String(user?.role || '')) && (
                          <Tooltip title="Send Login Email (Resets Password)">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={(e) => handleResetPasswordAndEmail(user, e)}
                            >
                              <Email />
                            </IconButton>
                          </Tooltip>
                        )}
                        {isAdmin && (
                          <Tooltip title="Delete User">
                            <IconButton 
                              size="small" 
                              color="error"
                              onClick={(e) => handleDeleteUser(user, e)}
                            >
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )
      )}

      {/* Add/Edit User Dialog */}
      <Dialog 
        open={openDialog && (dialogType === 'add' || dialogType === 'edit')} 
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="md"
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{
          sx: {
            maxHeight: { xs: '100dvh', sm: 'calc(100vh - 64px)' }
          }
        }}
      >
        <DialogTitle>
          {dialogType === 'add' ? 'Add New User' : 'Edit User'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                autoFocus
                margin="dense"
                name="firstName"
                label="First Name"
                type="text"
                fullWidth
                value={userFormData.firstName}
                onChange={handleUserFormChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                margin="dense"
                name="lastName"
                label="Last Name"
                type="text"
                fullWidth
                value={userFormData.lastName}
                onChange={handleUserFormChange}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                margin="dense"
                name="email"
                label="Email Address"
                type="email"
                fullWidth
                value={userFormData.email}
                onChange={handleUserFormChange}
                required
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Email />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth margin="dense">
                <InputLabel id="role-select-label">Role</InputLabel>
                <Select
                  labelId="role-select-label"
                  name="role"
                  value={userFormData.role}
                  label="Role"
                  onChange={handleUserFormChange}
                >
                  <MenuItem value="admin">Admin</MenuItem>
                  <MenuItem value="operator">Operator</MenuItem>
                  <MenuItem value="super_user">Super User</MenuItem>
                  <MenuItem value="end_user">User</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  flexWrap: 'wrap',
                  minHeight: 56,
                  mt: 1
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={userFormData.active}
                      onChange={handleUserFormChange}
                      name="active"
                      color="primary"
                    />
                  }
                  label="Active"
                  sx={{ m: 0 }}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={userFormData.allowEmailNotifications}
                      onChange={handleUserFormChange}
                      name="allowEmailNotifications"
                      color="primary"
                    />
                  }
                  label="Email Alerts"
                  sx={{ m: 0 }}
                />
              </Box>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>Associate with Accounts</Typography>
              <Autocomplete
                multiple
                options={accounts}
                getOptionLabel={(option) => option.name || ''}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                value={accounts.filter(account => userFormData.accountIds.includes(String(account.id)))}
                onChange={handleAccountChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select accounts"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>Associate with Sites</Typography>
                {/* Only show this feature for admin and operator users */}
                {isAdminOrOperator && (
                  <FormControl sx={{ minWidth: 200, ml: 2 }}>
                    <Select
                      value=""
                      displayEmpty
                      size="small"
                      onChange={(e) => handleAddAllDevicesFromSite(e.target.value)}
                      renderValue={(selected) => {
                        if (selected === '') {
                          return <em>Add all devices from site</em>;
                        }
                        return selected;
                      }}
                    >
                      <MenuItem value="" disabled>
                        <em>Select a site</em>
                      </MenuItem>
                      {sites.map((site) => (
                        <MenuItem key={site.id} value={site.id}>
                          {site.description || site.id}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
              <Autocomplete
                multiple
                options={sites}
                getOptionLabel={(option) => option.description || ''}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                value={sites.filter(site => userFormData.siteIds.includes(String(site.id)))}
                onChange={handleSiteChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select sites"
                  />
                )}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="subtitle1" sx={{ mt: 1, mb: 1 }}>Associate with Devices</Typography>
              <Autocomplete
                multiple
                options={devices}
                getOptionLabel={(option) => {
                  const description = option?.description || '';
                  const serial = option?.serialNumber || '';
                  if (description && serial) return `${description} (${serial})`;
                  return description || serial || String(option?.id || '');
                }}
                isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
                value={devices.filter(device => userFormData.deviceIds.includes(String(device.id)))}
                onChange={handleDeviceChange}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    placeholder="Select devices"
                  />
                )}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleSubmitUser} 
            color="primary" 
            variant="contained"
            disabled={submitting}
          >
            {submitting ? 'Saving...' : (dialogType === 'add' ? 'Add User' : 'Save Changes')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={openDialog && dialogType === 'delete'}
        onClose={handleCloseDialog}
        fullScreen={isMobile}
        scroll="paper"
        maxWidth="xs"
        fullWidth
        PaperProps={{
          sx: {
            maxHeight: { xs: '100dvh', sm: 'calc(100vh - 64px)' }
          }
        }}
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the user <strong>{currentUser?.firstName} {currentUser?.lastName}</strong>? This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} color="inherit">
            Cancel
          </Button>
          <Button 
            onClick={handleDeleteSubmit} 
            color="error" 
            variant="contained"
            disabled={submitting}
          >
            {submitting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* CSV Import Dialog */}
      <Dialog
        open={csvImportOpen}
        onClose={handleCsvImportClose}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
        scroll="paper"
        PaperProps={{
          sx: {
            maxHeight: { xs: '100dvh', sm: 'calc(100vh - 64px)' }
          }
        }}
      >
        <DialogTitle>
          Import Users from CSV
          <Button
            startIcon={<Download />}
            onClick={downloadUsersTemplate}
            sx={{ float: 'right' }}
            size="small"
          >
            Download Template
          </Button>
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Upload a CSV file to import multiple users with site and device associations.
            <br /><br />
            <strong>Required columns:</strong> firstName, lastName, email, role, active
            <br />
            <strong>Optional columns:</strong> siteDescriptions (semicolon-separated), deviceSerialNumbers (semicolon-separated)
            <br /><br />
            <strong>Valid roles:</strong> admin, operator, super_user, end_user
          </DialogContentText>
          
          <Box sx={{ mb: 2 }}>
            <input
              accept=".csv"
              style={{ display: 'none' }}
              id="csv-file-input"
              type="file"
              onChange={handleCsvFileChange}
            />
            <label htmlFor="csv-file-input">
              <Button variant="outlined" component="span" startIcon={<Upload />}>
                Choose CSV File
              </Button>
            </label>
            {csvFile && (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Selected: {csvFile.name}
              </Typography>
            )}
          </Box>

          {csvPreview.length > 0 && (
            <Box>
              <Typography variant="h6" sx={{ mb: 1 }}>Preview:</Typography>
              <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      {csvPreview[0] && csvPreview[0].split(',').map((header, index) => (
                        <TableCell key={index}>
                          <strong>{header.replace(/"/g, '')}</strong>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {csvPreview.slice(1).map((row, index) => (
                      <TableRow key={index}>
                        {row.split(',').map((cell, cellIndex) => (
                          <TableCell key={cellIndex}>
                            {cell.replace(/"/g, '')}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCsvImportClose}>Cancel</Button>
          <Button 
            onClick={handleCsvImport} 
            variant="contained"
            disabled={!csvFile || csvImporting}
          >
            {csvImporting ? 'Importing...' : 'Import Users'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar 
        open={snackbar.open} 
        autoHideDuration={6000} 
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Users;
